import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function SupportForm() {
  const sectionRef = useRef(null);
  const formCardRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        formCardRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full min-h-[100vh] bg-[#2a1273] py-10 md:py-14 lg:py-16 relative overflow-hidden flex items-center"
    >
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-pumpkinToast-500/15 rounded-full blur-[110px]" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-[100px]" />

      {/* Subtle pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10 w-full">
        <div
          ref={formCardRef}
          className="grid grid-cols-1 lg:grid-cols-2 bg-white rounded-3xl shadow-2xl overflow-hidden opacity-0"
        >
          {/* Left - image side – removed forced aspect on lg, let it match form height */}
          <div className="relative min-h-[300px] lg:min-h-0 lg:h-full">
            <img
              src="/assets/plane.jpg"
              alt="Support"
              className="absolute inset-0 w-full h-full object-cover object-center" // ← object-center helps if cropping happens
            />
            <div className="absolute inset-0 bg-gradient-to-r lg:bg-gradient-to-r from-transparent to-white/20" />

            <div className="absolute inset-0 bg-[#2a1273]/50 flex items-center justify-center p-6 sm:p-8 lg:p-10">
              <div className="text-center text-white">
                <h3 className="font-AndoraModernSerif text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                  Need Guidance?
                </h3>
                <p className="text-white/90 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                  Reach out to our experts for personalized support and counseling.
                </p>
              </div>
            </div>
          </div>

          {/* Right - form side (unchanged) */}
          <div className="p-7 sm:p-9 lg:p-11 flex flex-col justify-center">
            <div className="mb-7">
              <span className="inline-flex items-center px-3 py-1 text-sm font-semibold uppercase tracking-widest text-pumpkinToast-500 bg-pumpkinToast-500/10 rounded-full border border-pumpkinToast-500/15 mb-4">
                Support
              </span>
              <h2 className="font-AndoraModernSerif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2a1273] mb-2">
                Get In Touch
              </h2>
              <p className="text-gray-600 text-base">Contact our team — we’ll respond quickly.</p>
            </div>

            <form className="flex flex-col gap-5">
              <input
                type="text"
                placeholder="Full Name"
                className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-pumpkinToast-400 focus:border-transparent transition-all"
              />
              <input
                type="email"
                placeholder="Email Address"
                className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-pumpkinToast-400 focus:border-transparent transition-all"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-pumpkinToast-400 focus:border-transparent transition-all"
              />
              <textarea
                placeholder="Your Message..."
                rows={4}
                className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-pumpkinToast-400 focus:border-transparent transition-all resize-none"
              />
              <button
                type="submit"
                className="w-full sm:w-auto sm:self-start px-9 py-3.5 bg-pumpkinToast-500 hover:bg-pumpkinToast-400 text-white font-semibold rounded-xl shadow-md shadow-pumpkinToast-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg mt-2 text-base"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
